<script lang="ts">
	/**
	 * `/Settings/Admin/Settings/Providers` — the configured payment providers.
	 *
	 * Providers are entities rather than environment variables (D17), which is what makes rotating
	 * a key without a redeploy, running a test-mode provider, and running two providers side by
	 * side during a migration possible at all (Design §4.5, §5.3).
	 */

	import { onMount } from 'svelte';
	import { Badge, Button, Modal } from 'flowbite-svelte';
	import {
		ConfirmButton,
		EmptyState,
		PageHeader,
		Panel,
		ProviderCredentialsForm,
		RelativeDate
	} from '../../components';
	import {
		createPaymentProvider,
		listPaymentProviderTypes,
		listPaymentProviders,
		makePaymentProviderDefault,
		reEncryptSecrets,
		updatePaymentProvider,
		verifyPaymentProvider
	} from '../../AppNetwork';
	import type {
		PaymentProviderConfig,
		PaymentProviderWriteInput,
		ProviderTypeDescriptor
	} from '../../AppTypes';
	import { toastError, toastSuccess, toastWarning } from '../../stores/toast';

	let providers: PaymentProviderConfig[] = [];
	let types: ProviderTypeDescriptor[] = [];
	let loading: boolean = true;
	let loadError: string = '';

	/** The row being edited, `null` for the create form, `undefined` for "no form open". */
	let editing: PaymentProviderConfig | null | undefined = undefined;
	let saving: boolean = false;
	let verifyingUUID: string = '';
	let promotingUUID: string = '';
	let reEncrypting: boolean = false;

	const HEALTH_TONE: Record<string, 'green' | 'red' | 'yellow'> = {
		healthy: 'green',
		unhealthy: 'red',
		undecryptable: 'red',
		unknown: 'yellow'
	};

	const HEALTH_LABEL: Record<string, string> = {
		healthy: 'Verified',
		unhealthy: 'Verification failed',
		undecryptable: 'Cannot decrypt',
		unknown: 'Not verified'
	};

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			const [providerResponse, typeResponse] = await Promise.all([
				listPaymentProviders(),
				listPaymentProviderTypes()
			]);
			providers = providerResponse.items;
			types = typeResponse.items;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	/** Keeps the open form pointed at the freshly-returned row rather than a stale copy. */
	function fnReplace(updated: PaymentProviderConfig): void {
		providers = providers.map(provider => (provider.uuid === updated.uuid ? updated : provider));
		if (editing && editing.uuid === updated.uuid) {
			editing = updated;
		}
	}

	async function fnSave(event: CustomEvent<{ input: PaymentProviderWriteInput }>): Promise<void> {
		saving = true;
		try {
			if (editing === null) {
				const created = await createPaymentProvider(event.detail.input);
				providers = [...providers, created];
				editing = created;
				toastSuccess(`${created.name} created.`, 'Verify its credentials before making it default.');
			} else if (editing) {
				const updated = await updatePaymentProvider(editing.uuid, event.detail.input);
				fnReplace(updated);
				toastSuccess(`${updated.name} saved.`);
			}
		} catch (error) {
			toastError('The provider was not saved.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	async function fnVerify(provider: PaymentProviderConfig): Promise<void> {
		verifyingUUID = provider.uuid;
		try {
			const checked = await verifyPaymentProvider(provider.uuid);
			fnReplace(checked);
			if (checked.healthStatus === 'healthy') {
				toastSuccess(`${checked.name} verified.`);
			} else {
				// A failure here is the point of the button, not an error in the request.
				toastWarning(`${checked.name} did not verify.`, checked.healthError ?? '');
			}
		} catch (error) {
			toastError('The verification could not be run.', (error as Error).message);
		} finally {
			verifyingUUID = '';
		}
	}

	async function fnMakeDefault(provider: PaymentProviderConfig): Promise<void> {
		promotingUUID = provider.uuid;
		try {
			await makePaymentProviderDefault(provider.uuid);
			// The promote clears another row too, so the whole list is re-read rather than patched.
			await fnLoad();
			toastSuccess(`${provider.name} now processes all new purchases.`);
		} catch (error) {
			toastError('The default provider was not changed.', (error as Error).message);
		} finally {
			promotingUUID = '';
		}
	}

	async function fnReEncrypt(): Promise<void> {
		reEncrypting = true;
		try {
			const result = await reEncryptSecrets();
			const versions = Object.entries(result.byKeyVersion)
				.map(([version, count]) => `${count} under ${version}`)
				.join(', ');
			if (result.failures.length > 0) {
				toastWarning(
					`${result.reEncrypted} of ${result.total} secrets re-encrypted.`,
					`${result.failures.length} could not be read: ${result.failures.map(failure => failure.uuid).join(', ')}`
				);
			} else {
				toastSuccess(
					`Re-encrypted under key version ${result.activeKeyVersion}.`,
					versions.length > 0 ? versions : 'There were no stored secrets.'
				);
			}
			await fnLoad();
		} catch (error) {
			toastError('The secrets were not re-encrypted.', (error as Error).message);
		} finally {
			reEncrypting = false;
		}
	}

	onMount(fnLoad);
</script>

<PageHeader title="Payment providers">
	<svelte:fragment slot="description">
		Credentials, health and the webhook URL for every provider this store can charge through.
	</svelte:fragment>
	<svelte:fragment slot="help">
		Credentials are encrypted at rest and are never returned by any endpoint, so reads show a mask.
		Exactly one provider may be the default, and it is the one every new purchase goes to.
	</svelte:fragment>
</PageHeader>

{#if loading}
	<EmptyState variant="loading" title="Loading payment providers…" />
{:else if loadError.length > 0}
	<EmptyState variant="error" title="Payment providers could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={fnLoad}>Try again</Button>
	</EmptyState>
{:else}
	<div class="space-y-4">
		<Panel title="Configured providers" faIcon="credit-card">
			<svelte:fragment slot="titleRight">
				<Button size="sm" color="primary" disabled={types.length === 0} on:click={() => (editing = null)}>
					<i class="fa-solid fa-plus mr-2" />Add provider
				</Button>
			</svelte:fragment>

			{#if types.length === 0}
				<EmptyState
					title="This build registers no payment adapters."
					description="A provider row can only be created for an adapter the running build knows about."
				/>
			{:else if providers.length === 0}
				<EmptyState
					title="No payment provider is configured."
					description="The store cannot take money until one provider is active, default and verified."
				>
					<Button color="primary" size="sm" on:click={() => (editing = null)}>
						Configure the first provider
					</Button>
				</EmptyState>
			{:else}
				<div class="space-y-2">
					{#each providers as provider (provider.uuid)}
						<div class="widget bg-depth depth-hover flex flex-wrap items-center gap-3">
							<div class="min-w-0 grow">
								<div class="flex flex-wrap items-center gap-2">
									<span class="font-semibold truncate">{provider.name}</span>
									<Badge color={provider.mode === 'live' ? 'red' : 'blue'}>
										{provider.mode === 'live' ? 'LIVE' : 'TEST'}
									</Badge>
									{#if provider.isDefault}
										<Badge color="green">Default</Badge>
									{/if}
									{#if !provider.isActive}
										<Badge color="dark">Inactive</Badge>
									{/if}
									<Badge color={HEALTH_TONE[provider.healthStatus] ?? 'yellow'}>
										{HEALTH_LABEL[provider.healthStatus] ?? provider.healthStatus}
									</Badge>
								</div>
								<p class="text-xs text-default mt-1">
									{provider.type}
									{#if provider.healthCheckedAt}
										· checked <RelativeDate date={provider.healthCheckedAt} size="xs" />
									{/if}
								</p>
							</div>

							<div class="flex flex-wrap items-center gap-2">
								<Button
									size="xs"
									color="alternative"
									disabled={verifyingUUID === provider.uuid}
									on:click={() => fnVerify(provider)}
								>
									{#if verifyingUUID === provider.uuid}
										<i class="fa-solid fa-spinner fa-spin mr-1" />
									{:else}
										<i class="fa-solid fa-circle-check mr-1" />
									{/if}
									Verify
								</Button>

								{#if !provider.isDefault}
									<ConfirmButton
										size="xs"
										color="primary"
										disabled={promotingUUID === provider.uuid || !provider.isActive}
										confirmText={`Make “${provider.name}” the default provider? All new purchases will be processed by it.`}
										on:confirm={() => fnMakeDefault(provider)}
									>
										<i class="fa-solid fa-star mr-1" />Make default
									</ConfirmButton>
								{/if}

								<Button size="xs" color="alternative" on:click={() => (editing = provider)}>
									<i class="fa-solid fa-pen mr-1" />Edit
								</Button>
							</div>
						</div>
					{/each}
				</div>
			{/if}
		</Panel>

		<!--
			The credentials form owns its own fields and actions, so it is wrapped rather than
			rebuilt. `dismissable` is off while a save is in flight: the write is already on its way,
			and dismissing would leave the admin unsure whether it landed.
		-->
		<Modal
			open={editing !== undefined}
			size="lg"
			outsideclose={!saving}
			dismissable={!saving}
			title=""
			on:close={() => {
				if (!saving) editing = undefined;
			}}
		>
			{#if editing !== undefined}
				<ProviderCredentialsForm
					provider={editing}
					{types}
					{saving}
					verifying={verifyingUUID === editing?.uuid}
					promoting={promotingUUID === editing?.uuid}
					on:save={fnSave}
					on:verify={() => editing && fnVerify(editing)}
					on:makeDefault={() => editing && fnMakeDefault(editing)}
					on:cancel={() => (editing = undefined)}
				/>
			{/if}
		</Modal>

		<Panel title="Stored secrets" faIcon="key" isHidden>
			<p class="text-sm text-default mb-3">
				Every credential is encrypted under a key version from the deployment's keyring. After
				adding a new key version, re-encrypt so that no row is still readable only by the old key,
				after which it is safe to drop that key from the environment. The result reports how many rows
				sit under each version, so the rotation is observably complete rather than assumed.
			</p>
			<ConfirmButton
				size="sm"
				color="alternative"
				disabled={reEncrypting}
				confirmText="Re-encrypt every stored credential under the active key version?"
				on:confirm={fnReEncrypt}
			>
				{#if reEncrypting}
					<i class="fa-solid fa-spinner fa-spin mr-2" />
				{:else}
					<i class="fa-solid fa-rotate mr-2" />
				{/if}
				Re-encrypt secrets
			</ConfirmButton>
		</Panel>
	</div>
{/if}
